import type { SessionSummary } from "@herskin/contracts";
import {
  IconArchive as Archive,
  IconArchiveOff as Restore,
  IconChevronDown as ChevronDown,
  IconCopy as Copy,
  IconDots as MoreHorizontal,
  IconEdit as Edit,
  IconMessageCircle as MessageSquareText,
  IconSearch as Search,
  IconTrash as Trash,
  IconX as X,
} from "@tabler/icons-react";
import { memo, useEffect, useMemo, useRef, useState, type FormEvent, type MouseEvent } from "react";
import { createPortal } from "react-dom";
import { copyText } from "./clipboardCopy";
import { groupSessionsByDate, statusLabel, type SessionDateGroup } from "./presentation";

type SessionActionHandler = (session: SessionSummary) => Promise<boolean>;
type RenameSessionHandler = (session: SessionSummary, title: string) => Promise<boolean>;

interface SessionGroupsProps {
  sessions: SessionSummary[];
  selectedId: string | null;
  busySessionId?: string | null;
  onSelect: (sessionId: string) => void;
  onRename?: RenameSessionHandler;
  onArchive?: SessionActionHandler;
  onRestore?: SessionActionHandler;
  onDelete?: SessionActionHandler;
  searchOpen?: boolean;
  onSearchClose?: () => void;
  now?: Date;
}

interface MenuState {
  sessionId: string;
  x: number;
  y: number;
}

interface DialogState {
  kind: "rename" | "delete";
  sessionId: string;
}

const MENU_WIDTH = 208;
const MENU_HEIGHT = 196;
const RUNNING_FILTER_STATUSES = new Set([
  "submitting",
  "accepted",
  "running",
  "recovering",
  "stopping",
]);

type SessionFilter = "all" | "running" | "waiting";

function lifecycleOf(session: SessionSummary) {
  return session.management?.lifecycle ?? "active";
}

function boundedMenuPosition(x: number, y: number) {
  return {
    x: Math.max(8, Math.min(x, window.innerWidth - MENU_WIDTH - 8)),
    y: Math.max(8, Math.min(y, window.innerHeight - MENU_HEIGHT - 8)),
  };
}

export const SessionGroups = memo(function SessionGroups({
  sessions,
  selectedId,
  busySessionId = null,
  onSelect,
  onRename,
  onArchive,
  onRestore,
  onDelete,
  searchOpen = false,
  onSearchClose,
  now,
}: SessionGroupsProps) {
  const [collapsed, setCollapsed] = useState<Set<SessionDateGroup>>(() => new Set());
  const [archivedCollapsed, setArchivedCollapsed] = useState(true);
  const [menu, setMenu] = useState<MenuState | null>(null);
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [renameTitle, setRenameTitle] = useState("");
  const [copyFeedback, setCopyFeedback] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<SessionFilter>("all");
  const renameInput = useRef<HTMLInputElement>(null);
  const searchInput = useRef<HTMLInputElement>(null);

  const normalizedSearch = search.trim().toLocaleLowerCase("zh-CN");
  const activeSessions = useMemo(() => {
    return sessions.filter((session) => {
      if (lifecycleOf(session) !== "active") return false;
      const matchesSearch =
        !normalizedSearch ||
        session.title.toLocaleLowerCase("zh-CN").includes(normalizedSearch) ||
        session.preview.toLocaleLowerCase("zh-CN").includes(normalizedSearch) ||
        session.agentSessionId?.toLocaleLowerCase("zh-CN").includes(normalizedSearch);
      if (!matchesSearch) return false;
      if (filter === "waiting") {
        return ["waiting_approval", "waiting_clarification"].includes(
          session.activeRunStatus ?? "",
        );
      }
      if (filter === "running") {
        return RUNNING_FILTER_STATUSES.has(session.activeRunStatus ?? "");
      }
      return true;
    });
  }, [filter, normalizedSearch, sessions]);
  const archivedSessions = useMemo(
    () =>
      filter === "all"
        ? sessions.filter(
            (session) =>
              lifecycleOf(session) === "archived" &&
              (!normalizedSearch ||
                session.title.toLocaleLowerCase("zh-CN").includes(normalizedSearch) ||
                session.preview.toLocaleLowerCase("zh-CN").includes(normalizedSearch) ||
                session.agentSessionId?.toLocaleLowerCase("zh-CN").includes(normalizedSearch)),
          )
        : [],
    [filter, normalizedSearch, sessions],
  );
  const groups = useMemo(() => groupSessionsByDate(activeSessions, now), [activeSessions, now]);
  const menuSession = menu
    ? (sessions.find((session) => session.id === menu.sessionId) ?? null)
    : null;
  const dialogSession = dialog
    ? (sessions.find((session) => session.id === dialog.sessionId) ?? null)
    : null;

  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    document.addEventListener("click", close);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
      document.removeEventListener("click", close);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menu]);

  useEffect(() => {
    if (!dialog) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && busySessionId !== dialog.sessionId) setDialog(null);
    };
    document.addEventListener("keydown", onKeyDown);
    if (dialog.kind === "rename") requestAnimationFrame(() => renameInput.current?.focus());
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [busySessionId, dialog]);

  useEffect(() => {
    if (menu && !menuSession) setMenu(null);
    if (dialog && !dialogSession) setDialog(null);
  }, [dialog, dialogSession, menu, menuSession]);

  useEffect(() => {
    if (searchOpen) searchInput.current?.focus();
    else setSearch("");
  }, [searchOpen]);

  const closeSearch = () => {
    setSearch("");
    onSearchClose?.();
  };

  const toggleGroup = (groupId: SessionDateGroup) => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const openMenuAt = (sessionId: string, x: number, y: number) => {
    const position = boundedMenuPosition(x, y);
    setMenu({ sessionId, ...position });
  };

  const openContextMenu = (event: MouseEvent<HTMLElement>, sessionId: string) => {
    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    openMenuAt(sessionId, event.clientX || rect.right, event.clientY || rect.top + 12);
  };

  const openButtonMenu = (event: MouseEvent<HTMLButtonElement>, sessionId: string) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    openMenuAt(sessionId, rect.right - MENU_WIDTH, rect.bottom + 4);
  };

  const openDialog = (kind: DialogState["kind"], session: SessionSummary) => {
    setMenu(null);
    setDialog({ kind, sessionId: session.id });
    if (kind === "rename") setRenameTitle(session.title);
  };

  const submitRename = async (event: FormEvent) => {
    event.preventDefault();
    if (!dialogSession || !onRename) return;
    const title = renameTitle.trim();
    if (!title || title === dialogSession.title) return;
    await onRename(dialogSession, title);
    // 错误详情呈现在侧栏反馈区；关闭弹窗避免遮住服务端返回的真实信息。
    setDialog(null);
  };

  const confirmDelete = async () => {
    if (!dialogSession || !onDelete) return;
    await onDelete(dialogSession);
    setDialog(null);
  };

  const copyAgentSessionId = async (session: SessionSummary) => {
    if (!session.agentSessionId) return;
    setMenu(null);
    const result = await copyText(session.agentSessionId);
    setCopyFeedback(result.ok ? "Session ID 已复制" : "Session ID 复制失败，请稍后重试");
  };

  const renderSession = (session: SessionSummary) => {
    const archived = lifecycleOf(session) === "archived";
    const busy = busySessionId === session.id;
    const waitingForUser = ["waiting_approval", "waiting_clarification"].includes(
      session.activeRunStatus ?? "",
    );
    return (
      <div
        key={session.id}
        className={`session-item ${session.id === selectedId ? "session-item--active" : ""} ${archived ? "session-item--archived" : ""}`}
        onContextMenu={(event) => openContextMenu(event, session.id)}
      >
        <button
          type="button"
          className="session-select"
          onClick={() => onSelect(session.id)}
          aria-current={session.id === selectedId ? "page" : undefined}
        >
          <span className="session-icon">
            {archived ? <Archive size={16} /> : <MessageSquareText size={16} />}
          </span>
          <span className="session-copy">
            <strong>{session.title}</strong>
            <small>{archived ? "已归档 · 可恢复" : session.preview || "尚未开始对话"}</small>
          </span>
          {session.unreadCount ? <span className="unread-count">{session.unreadCount}</span> : null}
          {waitingForUser ? <span className="session-waiting-badge">等待我</span> : null}
          {session.activeRunStatus ? (
            <span className="running-dot" title={statusLabel(session.activeRunStatus)} />
          ) : null}
        </button>
        <button
          type="button"
          className="session-more"
          aria-label={`更多会话操作：${session.title}`}
          aria-haspopup="menu"
          aria-expanded={menu?.sessionId === session.id}
          disabled={busy}
          onClick={(event) => openButtonMenu(event, session.id)}
        >
          <MoreHorizontal size={17} />
        </button>
      </div>
    );
  };

  return (
    <>
      {copyFeedback ? (
        <div
          className="session-copy-feedback"
          role={copyFeedback.includes("失败") ? "alert" : "status"}
        >
          {copyFeedback}
        </div>
      ) : null}
      <div className="session-list-tools">
        {searchOpen ? (
          <div className="session-search" role="search">
            <Search size={15} aria-hidden="true" />
            <input
              ref={searchInput}
              type="search"
              aria-label="搜索会话"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== "Escape") return;
                event.preventDefault();
                closeSearch();
              }}
              placeholder="搜索会话标题或摘要"
            />
            {search ? (
              <button
                type="button"
                className="session-search-clear"
                aria-label="清空会话搜索"
                onClick={() => {
                  setSearch("");
                  searchInput.current?.focus();
                }}
              >
                <X size={14} aria-hidden="true" />
              </button>
            ) : null}
          </div>
        ) : null}
        <div className="session-filter" role="group" aria-label="筛选会话">
          {(
            [
              ["all", "全部"],
              ["running", "运行中"],
              ["waiting", "等待我"],
            ] as const
          ).map(([value, label]) => (
            <button
              type="button"
              key={value}
              aria-pressed={filter === value}
              onClick={() => setFilter(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="session-groups-scroll">
        {groups.map((group) => {
          const isCollapsed = collapsed.has(group.id);
          const contentId = `session-group-${group.id}`;
          return (
            <section className="session-group" key={group.id}>
              <button
                type="button"
                className="session-group-toggle"
                aria-label={`${isCollapsed ? "展开" : "折叠"}${group.label}会话`}
                aria-expanded={!isCollapsed}
                aria-controls={contentId}
                onClick={() => toggleGroup(group.id)}
              >
                <span>{group.label}</span>
                <span className="session-group-count">{group.sessions.length}</span>
                <ChevronDown
                  className={
                    isCollapsed
                      ? "session-group-chevron session-group-chevron--collapsed"
                      : "session-group-chevron"
                  }
                  size={14}
                />
              </button>
              {!isCollapsed ? (
                <div id={contentId} className="session-group-items">
                  {group.sessions.map(renderSession)}
                </div>
              ) : null}
            </section>
          );
        })}

        {archivedSessions.length ? (
          <section className="session-group session-group--archived">
            <button
              type="button"
              className="session-group-toggle"
              aria-label={`${archivedCollapsed ? "展开" : "折叠"}已归档会话`}
              aria-expanded={!archivedCollapsed}
              aria-controls="session-group-archived"
              onClick={() => setArchivedCollapsed((value) => !value)}
            >
              <span>已归档</span>
              <span className="session-group-count">{archivedSessions.length}</span>
              <ChevronDown
                className={
                  archivedCollapsed
                    ? "session-group-chevron session-group-chevron--collapsed"
                    : "session-group-chevron"
                }
                size={14}
              />
            </button>
            {!archivedCollapsed ? (
              <div id="session-group-archived" className="session-group-items">
                {archivedSessions.map(renderSession)}
              </div>
            ) : null}
          </section>
        ) : null}
        {!groups.length && !archivedSessions.length ? (
          <div className="session-filter-empty">
            {normalizedSearch ? "没有找到匹配的会话" : "没有符合条件的会话"}
          </div>
        ) : null}
      </div>

      {menu && menuSession
        ? createPortal(
            <div
              className="session-menu"
              role="menu"
              aria-label={`会话操作：${menuSession.title}`}
              style={{ left: menu.x, top: menu.y }}
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => openDialog("rename", menuSession)}
              >
                <Edit size={16} />
                重命名
              </button>
              <button
                type="button"
                role="menuitem"
                disabled={!menuSession.agentSessionId}
                title={
                  menuSession.agentSessionId
                    ? "复制 Hermes Session ID"
                    : "首次发送后生成 Session ID"
                }
                onClick={() => void copyAgentSessionId(menuSession)}
              >
                <Copy size={16} />
                复制 Session ID
              </button>
              {lifecycleOf(menuSession) === "archived" ? (
                <button
                  type="button"
                  role="menuitem"
                  disabled={!onRestore}
                  onClick={() => {
                    setMenu(null);
                    void onRestore?.(menuSession);
                  }}
                >
                  <Restore size={16} />
                  恢复会话
                </button>
              ) : (
                <button
                  type="button"
                  role="menuitem"
                  disabled={!onArchive}
                  onClick={() => {
                    setMenu(null);
                    void onArchive?.(menuSession);
                  }}
                >
                  <Archive size={16} />
                  归档会话
                </button>
              )}
              <div className="session-menu-separator" role="separator" />
              <button
                type="button"
                role="menuitem"
                className="session-menu-danger"
                onClick={() => openDialog("delete", menuSession)}
              >
                <Trash size={16} />
                删除会话
              </button>
            </div>,
            document.body,
          )
        : null}

      {dialog && dialogSession
        ? createPortal(
            <div
              className="session-dialog-backdrop"
              role="presentation"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget && busySessionId !== dialogSession.id)
                  setDialog(null);
              }}
            >
              <section
                className="session-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="session-dialog-title"
              >
                {dialog.kind === "rename" ? (
                  <form onSubmit={submitRename}>
                    <h2 id="session-dialog-title">重命名会话</h2>
                    <p>名称由 Hermes 保存；若暂时断线，HerSkin 会先保留更改并在重连后同步。</p>
                    <label htmlFor="session-rename-title">会话名称</label>
                    <input
                      ref={renameInput}
                      id="session-rename-title"
                      value={renameTitle}
                      maxLength={100}
                      onChange={(event) => setRenameTitle(event.target.value)}
                    />
                    <div className="session-dialog-actions">
                      <button
                        type="button"
                        className="session-dialog-secondary"
                        disabled={busySessionId === dialogSession.id}
                        onClick={() => setDialog(null)}
                      >
                        取消
                      </button>
                      <button
                        type="submit"
                        className="session-dialog-primary"
                        disabled={
                          !renameTitle.trim() ||
                          renameTitle.trim() === dialogSession.title ||
                          busySessionId === dialogSession.id
                        }
                      >
                        保存名称
                      </button>
                    </div>
                  </form>
                ) : (
                  <div>
                    <h2 id="session-dialog-title">确认删除“{dialogSession.title}”？</h2>
                    <p>
                      这会删除 HerSkin 与 Hermes
                      中的会话；若暂时断线，会在重连后继续同步删除。此操作无法撤销。
                    </p>
                    <div className="session-dialog-actions">
                      <button
                        type="button"
                        className="session-dialog-secondary"
                        disabled={busySessionId === dialogSession.id}
                        onClick={() => setDialog(null)}
                      >
                        取消
                      </button>
                      <button
                        type="button"
                        className="session-dialog-danger"
                        disabled={busySessionId === dialogSession.id}
                        onClick={() => void confirmDelete()}
                      >
                        确认删除
                      </button>
                    </div>
                  </div>
                )}
              </section>
            </div>,
            document.body,
          )
        : null}
    </>
  );
});
